import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Text,
  Image,
  Loader,
  Badge,
  Paper,
  PasswordInput,
  Title,
  Notification,
} from "@mantine/core";
import "./app.css";
import { useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, chatFunctions, signInWithEmailAndPassword, applyActionCode } from "../firebase";

export function ConfirmEmail() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "verifyEmail";
  const actionCode = searchParams.get("oobCode") || "";
  const loginUrl =
    typeof window !== "undefined" &&
    (window.location.hostname === "captivadata-user-action.web.app" ||
      window.location.hostname === "captivadata-user-action.firebaseapp.com")
      ? "https://captivadata.app/"
      : "/";
  const resetUrl =
    typeof window !== "undefined" &&
    (window.location.hostname === "captivadata-user-action.web.app" ||
      window.location.hostname === "captivadata-user-action.firebaseapp.com")
      ? "https://captivadata.app/reset"
      : "/reset";

  const [loading, setLoading] = useState(Boolean(actionCode));
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submittingPassword, setSubmittingPassword] = useState(false);
  const [provisionedCompanyName, setProvisionedCompanyName] = useState("");

  useEffect(() => {
    if (!actionCode) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    if (mode === "resetPassword") {
      verifyPasswordResetCode(auth, actionCode)
        .then((email) => {
          setResetEmail(email || "");
          setLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setErrorMessage("This password setup link is invalid or has already been used.");
          setLoading(false);
        });
      return;
    }

    applyActionCode(auth, actionCode)
      .then(async () => {
        try {
          await auth.currentUser?.reload();
        } catch {
          // ignore reload errors
        }
        setLoading(false);
        setSuccess(true);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, [actionCode, mode]);

  const handlePasswordSubmit = async () => {
    setErrorMessage("");
    if (newPassword.length < 8) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    setSubmittingPassword(true);
    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      if (resetEmail) {
        try {
          await signInWithEmailAndPassword(auth, resetEmail, newPassword);
          const completeFn = httpsCallable<
            Record<string, never>,
            { completed?: boolean; companyName?: string }
          >(chatFunctions, "completeWhatsAppOnboarding");
          const res = await completeFn({});
          if (res.data?.completed && res.data?.companyName) {
            setProvisionedCompanyName(res.data.companyName);
          }
        } catch (onboardingErr) {
          console.warn("WhatsApp onboarding completion check skipped or non-fatal:", onboardingErr);
        }
      }
      setSuccess(true);
    } catch (err: any) {
      setErrorMessage(err?.message || "Failed to set password. The link may have expired.");
    } finally {
      setSubmittingPassword(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        backgroundColor: "#f8f9fa",
      }}
    >
      <Paper
        shadow="sm"
        radius="md"
        p="xl"
        withBorder
        sx={{ width: "100%", maxWidth: 420, backgroundColor: "#ffffff" }}
      >
        <Stack spacing="md" align="center">
          <Image
            radius="md"
            src="/captiva-mark.png"
            style={{ height: "5.5em", width: "5.5em", margin: "auto" }}
          />
          {loading ? (
            <>
              <Loader style={{ margin: "1em auto" }} color="cap-navy" />
              <Text align="center">
                {mode === "resetPassword" ? "Verifying password link..." : "Verifying your email..."}
              </Text>
            </>
          ) : mode === "resetPassword" ? (
            success ? (
              <Stack spacing="md" sx={{ width: "100%" }}>
                <Badge variant="filled" fullWidth color="green" size="xl">
                  {provisionedCompanyName ? "Password saved & Workspace ready!" : "Password saved!"}
                </Badge>
                <Text size="md" align="center">
                  {provisionedCompanyName
                    ? `Your Captiva password has been set and your workspace for ${provisionedCompanyName} is ready! Check WhatsApp for your login link, or close this window to return to WhatsApp.`
                    : "Your Captiva password has been set. You can now close this window to return to WhatsApp or the Captiva app, or continue to login below."}
                </Text>
                <Button
                  color="cap-navy"
                  size="md"
                  fullWidth
                  onClick={() => {
                    window.location.href = loginUrl;
                  }}
                >
                  Continue to Login
                </Button>
              </Stack>
            ) : resetEmail ? (
              <Stack spacing="md" sx={{ width: "100%" }}>
                <Title order={3} align="center">
                  Set Your Password
                </Title>
                <Text size="sm" color="dimmed" align="center">
                  Setting password for <b>{resetEmail}</b>
                </Text>
                {errorMessage && (
                  <Notification color="red" onClose={() => setErrorMessage("")}>
                    {errorMessage}
                  </Notification>
                )}
                <PasswordInput
                  required
                  size="md"
                  label="New Password"
                  placeholder="Minimum 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.currentTarget.value)}
                />
                <PasswordInput
                  required
                  size="md"
                  label="Confirm Password"
                  placeholder="Retype your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                />
                <Button
                  color="cap-navy"
                  size="md"
                  fullWidth
                  disabled={!newPassword || !confirmPassword || submittingPassword}
                  onClick={handlePasswordSubmit}
                >
                  {submittingPassword ? <Loader size="sm" color="white" /> : "Save Password"}
                </Button>
              </Stack>
            ) : (
              <Stack spacing="md" sx={{ width: "100%" }}>
                <Badge variant="filled" fullWidth color="red" size="xl">
                  Link expired or invalid
                </Badge>
                <Text size="sm" align="center" color="dimmed">
                  {errorMessage || "This password link is no longer valid. You can request a new one on the login page."}
                </Text>
                <Button
                  color="cap-navy"
                  size="md"
                  fullWidth
                  onClick={() => {
                    window.location.href = resetUrl;
                  }}
                >
                  Reset Password
                </Button>
              </Stack>
            )
          ) : success ? (
            <Stack spacing="md" sx={{ width: "100%" }}>
              <Badge variant="filled" fullWidth color="green" size="xl">
                Email verified!
              </Badge>
              <Text size="md" align="center">
                Your email address has been verified! You can now close this window to return to WhatsApp or the Captiva app.
              </Text>
            </Stack>
          ) : (
            <Stack spacing="md" sx={{ width: "100%" }}>
              <Badge variant="filled" fullWidth color="red" size="xl">
                Email not verified
              </Badge>
              <Text size="sm" align="center" color="dimmed">
                This verification link has expired or has already been used. You can close this window and return to WhatsApp or the Captiva app.
              </Text>
              <Text size="xs" align="center" color="dimmed">
                Need help? Contact us at admin@captivadata.com
              </Text>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
